import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import type { PackagingMinimisationAssessmentStoredEntry } from '@/shared/hooks/api/packagingSuppliers'
import { pickPackagingAssessmentProse } from './pickPackagingAssessmentProse'

const STANDARD_CRITERION_KEYS = [
  { key: 'productProtection', labelKey: 'productProtection' },
  {
    key: 'packagingManufacturingProcess',
    labelKey: 'packagingManufacturingProcess',
  },
  { key: 'packingFillingProcess', labelKey: 'packingFillingProcess' },
  { key: 'logistics', labelKey: 'logistics' },
  {
    key: 'productPresentationAndMarketing',
    labelKey: 'productPresentationAndMarketing',
  },
  { key: 'userConsumerAcceptance', labelKey: 'userConsumerAcceptance' },
  { key: 'informationPC', labelKey: 'informationPC' },
  { key: 'safetyPC', labelKey: 'safetyPC' },
] as const

function countCriticalAreas(
  entry: PackagingMinimisationAssessmentStoredEntry
): number {
  let count = 0
  for (const { key } of STANDARD_CRITERION_KEYS) {
    if (entry.criteria[key].isCriticalArea) {
      count += 1
    }
  }
  return count
}

export function getMinimisationAssessmentRunSummary(
  entry: PackagingMinimisationAssessmentStoredEntry
): {
  criticalAreaCount: number
  weightGrams: number | null
  volumeCm3: number | null
  assessmentVersion: string
} {
  return {
    criticalAreaCount: countCriticalAreas(entry),
    weightGrams: entry.outcome.totalMinimumNecessaryWeightGrams,
    volumeCm3: entry.outcome.totalMinimumNecessaryVolumeCm3,
    assessmentVersion: entry.assessmentVersion,
  }
}

export function formatRunTimestamp(timestamp: string, locale: string): string {
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) {
    return timestamp
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

type PackagingMinimisationAssessmentRunResultsProps = {
  result: PackagingMinimisationAssessmentStoredEntry
  title?: string
}

export function PackagingMinimisationAssessmentRunResults({
  result,
  title,
}: PackagingMinimisationAssessmentRunResultsProps) {
  const { t, i18n } = useTranslation('packagingSuppliers')
  const useChinese = i18n.language.startsWith('zh')
  const criticalAreaCount = countCriticalAreas(result)
  const weightGrams = result.outcome.totalMinimumNecessaryWeightGrams
  const volumeCm3 = result.outcome.totalMinimumNecessaryVolumeCm3
  const outcomeReasoning = pickPackagingAssessmentProse({
    en: 'reasoningEn' in result.outcome ? result.outcome.reasoningEn : undefined,
    zh: 'reasoningZh' in result.outcome ? result.outcome.reasoningZh : undefined,
    legacy: 'reasoning' in result.outcome ? result.outcome.reasoning : undefined,
    useChinese,
  })

  return (
    <div className='space-y-4 rounded-lg border p-4'>
      {title ? (
        <h3 className='text-sm font-medium text-foreground'>{title}</h3>
      ) : null}
      <div className='flex flex-wrap items-center gap-2'>
        <Badge variant='outline'>
          {t('itemDetail.minimisation.criticalAreasCount', {
            count: criticalAreaCount,
          })}
        </Badge>
        <Badge variant='outline'>
          {t('itemDetail.minimisation.minimumWeight', {
            value:
              weightGrams != null
                ? t('itemDetail.minimisation.weightGrams', {
                    grams: weightGrams,
                  })
                : t('itemDetail.minimisation.notDetermined'),
          })}
        </Badge>
        <Badge variant='outline'>
          {t('itemDetail.minimisation.minimumVolume', {
            value:
              volumeCm3 != null
                ? t('itemDetail.minimisation.volumeCm3', {
                    volume: volumeCm3,
                  })
                : t('itemDetail.minimisation.notDetermined'),
          })}
        </Badge>
        <Badge variant='secondary'>
          {t('itemDetail.minimisation.version', {
            version: result.assessmentVersion,
          })}
        </Badge>
      </div>

      <div className='space-y-2'>
        <p className='text-sm font-medium'>
          {t('itemDetail.minimisation.outcomeSection')}
        </p>
        <p className='text-sm text-muted-foreground whitespace-pre-wrap'>
          {outcomeReasoning}
        </p>
      </div>

      <div className='space-y-3'>
        <p className='text-sm font-medium'>
          {t('itemDetail.minimisation.criteriaSection')}
        </p>
        {STANDARD_CRITERION_KEYS.map(({ key, labelKey }) => {
          const criterion = result.criteria[key]
          const explanation = pickPackagingAssessmentProse({
            en:
              'explanationEn' in criterion ? criterion.explanationEn : undefined,
            zh:
              'explanationZh' in criterion ? criterion.explanationZh : undefined,
            legacy:
              'explanation' in criterion ? criterion.explanation : undefined,
            useChinese,
          })
          const showCriticalBadge = criterion.isCriticalArea
          const showNotCriticalBadge = !criterion.isCriticalArea
          return (
            <div key={key} className='space-y-1 rounded-md border px-3 py-2'>
              <div className='flex flex-wrap items-center gap-2'>
                <span className='text-sm font-medium'>
                  {t(`itemDetail.minimisation.criteria.${labelKey}`)}
                </span>
                {showCriticalBadge && (
                  <Badge variant='secondary'>
                    {t('itemDetail.minimisation.criticalAreaYes')}
                  </Badge>
                )}
                {showNotCriticalBadge && (
                  <Badge variant='outline'>
                    {t('itemDetail.minimisation.criticalAreaNo')}
                  </Badge>
                )}
              </div>
              <p className='text-sm text-muted-foreground whitespace-pre-wrap'>
                {explanation}
              </p>
            </div>
          )
        })}
        <div className='space-y-1 rounded-md border px-3 py-2'>
          <span className='text-sm font-medium'>
            {t('itemDetail.minimisation.criteria.legislationPC')}
          </span>
          <p className='text-sm text-muted-foreground whitespace-pre-wrap'>
            {pickPackagingAssessmentProse({
              en:
                'explanationEn' in result.criteria.legislationPC
                  ? result.criteria.legislationPC.explanationEn
                  : undefined,
              zh:
                'explanationZh' in result.criteria.legislationPC
                  ? result.criteria.legislationPC.explanationZh
                  : undefined,
              legacy:
                'explanation' in result.criteria.legislationPC
                  ? result.criteria.legislationPC.explanation
                  : undefined,
              useChinese,
            })}
          </p>
        </div>
      </div>
    </div>
  )
}
